import { Link } from 'react-router-dom';

export default function SEOSection() {
  return (
    <section className="py-20 md:py-28 bg-secondary-500">
      <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
        {/* Tag */}
        <span className="inline-block px-4 py-1.5 bg-accent-100/20 text-accent-200 text-xs font-label font-medium rounded-lg mb-8">
          &frasl; SEO RESOURCE &frasl;
        </span>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="lg:w-[55%]">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-[1.2] mb-6">
              What Is Superbuy Spreadsheet?
            </h2>
            <div className="space-y-4">
              <p className="text-background-100/80 text-base leading-relaxed">
                <strong className="text-background-50">Superbuy Spreadsheet</strong> refers to organized product databases that compile thousands of curated finds across multiple shopping categories. These community-built resources bring structure to product discovery — transforming scattered product links into a browsable, searchable format that saves hours of manual research time.
              </p>
              <p className="text-background-100/70 text-base leading-relaxed">
                At its core, a <strong className="text-background-100/90">Superbuy spreadsheet</strong> acts as your personal shopping assistant. Each entry typically features product images, direct purchase links, sizing charts, quality control reference photos, and pricing comparisons — all neatly organized into categories like Shoes, Hoodies, Jackets, Accessories, and many more.
              </p>
              <p className="text-background-100/70 text-base leading-relaxed">
                The beauty of the <strong className="text-background-100/90">Superbuy spreadsheet</strong> system lies in its simplicity. Rather than endlessly scrolling through search results or jumping between dozens of seller pages, shoppers can browse a single organized document that presents products side by side. This makes comparison shopping, trend discovery, and quality research dramatically faster and more intuitive — especially for beginners who might feel overwhelmed by traditional shopping platforms.
              </p>
            </div>

            {/* Comparison Table */}
            <div className="mt-10 overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-background-50/10">
                    <th className="py-3 pr-6 text-sm font-label font-semibold text-background-50">Feature</th>
                    <th className="py-3 pr-6 text-sm font-label font-semibold text-background-100/60">Normal Search</th>
                    <th className="py-3 text-sm font-label font-semibold text-accent-400">Superbuy Spreadsheet</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-background-100/70">
                  <tr className="border-b border-background-50/5">
                    <td className="py-3 pr-6 font-medium text-background-100/90">Product Discovery</td>
                    <td className="py-3 pr-6">Manual searching</td>
                    <td className="py-3 text-accent-200">Organized categories</td>
                  </tr>
                  <tr className="border-b border-background-50/5">
                    <td className="py-3 pr-6 font-medium text-background-100/90">Finding Popular Items</td>
                    <td className="py-3 pr-6">Difficult</td>
                    <td className="py-3 text-accent-200">Easy browsing</td>
                  </tr>
                  <tr className="border-b border-background-50/5">
                    <td className="py-3 pr-6 font-medium text-background-100/90">Research Speed</td>
                    <td className="py-3 pr-6">Slow</td>
                    <td className="py-3 text-accent-200">Faster comparison</td>
                  </tr>
                  <tr className="border-b border-background-50/5">
                    <td className="py-3 pr-6 font-medium text-background-100/90">Product Tracking</td>
                    <td className="py-3 pr-6">No structure</td>
                    <td className="py-3 text-accent-200">Spreadsheet organization</td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-6 font-medium text-background-100/90">Beginner Usability</td>
                    <td className="py-3 pr-6">Complex</td>
                    <td className="py-3 text-accent-200">Simple</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Right Content */}
          <div className="lg:w-[45%] flex flex-col">
            <div className="relative rounded-2xl overflow-hidden bg-background-100/10 aspect-[4/3] mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20desk%20scene%20with%20an%20open%20notebook%20filled%20with%20handwritten%20product%20notes%20beside%20a%20ceramic%20mug%20of%20coffee%20a%20pair%20of%20reading%20glasses%20and%20a%20smartphone%20displaying%20colorful%20spreadsheet%20data%20soft%20afternoon%20sunlight%20streaming%20through%20a%20window%20with%20a%20potted%20succulent%20on%20the%20side%20editorial%20lifestyle%20photography%20productive%20cozy%20aesthetic%20rich%20warm%20tones%20organized%20workspace&width=800&height=600&seq=seo-flatlay-v2&orientation=landscape"
                alt="Superbuy Spreadsheet product research and organization concept"
                title="Superbuy Spreadsheet Shopping Guide"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="flex items-start gap-4">
              <span className="font-heading text-7xl md:text-8xl font-bold text-accent-400 leading-none shrink-0">
                01
              </span>
              <div>
                <p className="text-background-100/80 text-sm leading-relaxed">
                  Superbuy spreadsheet resources have grown into one of the most efficient ways to discover trending products. By organizing hundreds of curated finds into a single viewable format, these spreadsheets eliminate the guesswork from online shopping and put the best products right at your fingertips.
                </p>
                <Link
                  to="/articles/superbuy-spreadsheet-guide"
                  className="inline-flex items-center gap-2 mt-5 px-6 py-3 bg-background-50 text-secondary-600 font-label font-semibold text-sm rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Read Full Guide
                  <i className="ri-arrow-right-line text-base"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}